import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { getAllFeedback, updateFeedbackStatus, type Feedback } from '../../services/feedback.service';
import { LoadingSpinner } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';

export default function FeedbackReviewPage() {
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'new' | 'read' | 'reviewed'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    getAllFeedback()
      .then((items) => {
        setFeedback(items);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load feedback:', err);
        toast.error('Failed to load feedback entries.');
        setLoading(false);
      });
  }, []);

  const filtered =
    filter === 'all' ? feedback : feedback.filter((f) => f.status === filter);

  const handleStatusChange = async (id: string, status: 'new' | 'read' | 'reviewed') => {
    if (!user?.id) return;
    try {
      await updateFeedbackStatus(id, status, user.id);
      setFeedback((prev) =>
        prev.map((f) => (f.id === id ? { ...f, status, updated_by: user.id } : f))
      );
    } catch {
      toast.error('Failed to update status. Check your admin permissions.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner message="Loading feedback..." />
      </div>
    );
  }

  const tabs = [
    { key: 'all' as const, label: 'All', count: feedback.length },
    { key: 'new' as const, label: 'New', count: feedback.filter((f) => f.status === 'new').length },
    { key: 'read' as const, label: 'Read', count: feedback.filter((f) => f.status === 'read').length },
    {
      key: 'reviewed' as const,
      label: 'Reviewed',
      count: feedback.filter((f) => f.status === 'reviewed').length,
    },
  ];

  return (
    <div className="max-w-[400px] mx-auto py-6 space-y-4">
      <div>
        <h1 className="text-xl font-extrabold text-aura-text">Feedback</h1>
        <p className="text-xs text-aura-text/40 mt-0.5">
          {feedback.length} total submission{feedback.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 bg-aura-background rounded-xl p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all duration-200 ${
              filter === tab.key
                ? 'bg-white text-aura-primary shadow-sm'
                : 'text-aura-text/40 hover:text-aura-text/60'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Feedback list */}
      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-aura-text/30 font-medium text-sm">No feedback yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => {
            const isExpanded = expandedId === item.id;
            const displayText =
              isExpanded
                ? item.text
                : item.text.length > 120
                  ? item.text.slice(0, 120) + '...'
                  : item.text;

            return (
              <div
                key={item.id}
                className="bg-white rounded-xl p-4 shadow-sm border border-aura-text/5 space-y-2"
              >
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-aura-text/50 flex-wrap">
                    <span className="font-bold text-aura-text">{item.user_name}</span>
                    <span>·</span>
                    <span>
                      {item.created_at?.toDate?.().toLocaleDateString() || 'just now'}
                    </span>
                    {item.screen_name && (
                      <>
                        <span>·</span>
                        <span className="text-[10px] font-bold text-aura-text/40 bg-aura-background px-1.5 py-0.5 rounded-full">
                          {item.screen_name}
                        </span>
                        {item.has_form_data && (
                          <span className="text-[10px] font-medium text-aura-primary/50 bg-aura-primary/5 px-1.5 py-0.5 rounded-full">
                            form
                          </span>
                        )}
                      </>
                    )}
                    {item.is_voice && <span>🎤</span>}
                  </div>
                  <span
                    className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                      item.status === 'new'
                        ? 'bg-aura-primary/10 text-aura-primary'
                        : item.status === 'read'
                          ? 'bg-aura-text/5 text-aura-text/50'
                          : 'bg-aura-text/5 text-aura-text/30'
                    }`}
                  >
                    {item.status}
                  </span>
                </div>

                {/* Text */}
                <p
                  className="text-sm text-aura-text/80 leading-relaxed cursor-pointer"
                  onClick={() => setExpandedId(isExpanded ? null : (item.id ?? null))}
                >
                  {displayText}
                </p>

                {/* Summary (voice only) */}
                {item.is_voice && item.summary && (
                  <div className="bg-aura-primary/5 rounded-lg p-2.5 text-xs text-aura-text/60 italic border-l-2 border-aura-primary/20">
                    <span className="font-bold text-aura-primary/70 not-italic uppercase text-[9px] tracking-wider">
                      AI Summary:
                    </span>
                    <br />
                    {item.summary}
                  </div>
                )}

                {/* Status actions */}
                <div className="flex gap-2 pt-1">
                  {item.status !== 'reviewed' && (
                    <button
                      onClick={() => handleStatusChange(item.id!, 'reviewed')}
                      className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-aura-primary/10 text-aura-primary hover:bg-aura-primary/20 transition-colors"
                    >
                      Mark Reviewed
                    </button>
                  )}
                  {item.status === 'new' && (
                    <button
                      onClick={() => handleStatusChange(item.id!, 'read')}
                      className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-aura-text/5 text-aura-text/50 hover:bg-aura-text/10 transition-colors"
                    >
                      Mark Read
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
